import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  ListGroup,
} from 'react-bootstrap';
import { CheckCircle } from 'react-bootstrap-icons';
import TesterBudNavbar from '../../subComponent/TesterBudNavBar';
import HeroImage from '../icons/form-icon.png';

const RegistrationPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordRules = {
    minLength: 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  };

  const isPasswordValid = () => {
    return (
      password.length >= passwordRules.minLength &&
      passwordRules.hasUpperCase &&
      passwordRules.hasLowerCase &&
      passwordRules.hasNumber &&
      passwordRules.hasSpecialChar
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!email) return setError('Email is required');
    if (!password) return setError('Password is required');
    if (!confirmPassword) return setError('Please confirm your password');
    if (password !== confirmPassword) return setError('Passwords do not match');
    if (!isPasswordValid()) return setError('Password does not meet the requirements');

    setSuccess(true);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>Registration Automation Practice Page | Selenium, Cypress, Playwright</title>
        <meta http-equiv="Content-type" content="text/html;charset=UTF-8" />
        <meta name="description" content="Enhance your software testing skills with our free dummy registration page. Practice automating registration forms using Selenium, Cypress, and Playwright. Ideal for beginners learning UI and test automation." />
        <meta name="keywords" content="registration automation practice, selenium registration practice, cypress registration automation, playwright registration testing, dummy registration page, test registration automation, ui automation examples, registration form automation, web registration automation, practice website registration, free test automation practice, selenium practice registration form, automate registration functionality" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="TesterBud Team" />
        <meta property="og:title" content="Registration Automation Practice Page | Selenium, Cypress, Playwright" />
        <meta property="og:description" content="Enhance your software testing skills with our free dummy registration page. Practice automating registration forms using Selenium, Cypress, and Playwright. Ideal for beginners learning UI and test automation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://testerbud.com/practice-registration-automation" />
        <meta name="geo.region" content="GLOBAL" />
        <meta name="geo.placename" content="Global" />
      </Helmet>
      <Container fluid className="hero-section bg-primary text-white py-5" style={{ position: 'relative', overflow: 'hidden' }}>
        <Row className="align-items-center justify-content-center">
          <Col md={6} className="text-center text-md-start">
            <h1 className="display-4 fw-bold mb-3">Master Registration Automation with TesterBud</h1>
            <p className="lead mb-4">
              Hone your software testing expertise by practicing registration form automation on our free, interactive dummy registration page. Perfect for Selenium, Cypress, and Playwright learners.
            </p>
            <p className="lead mb-4">
              Develop hands-on skills, create robust test cases, and become a confident UI automation specialist. 🚀
            </p>
          </Col>
          <Col md={5} className="d-none d-md-block text-center">
            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
              <div
                style={{
                  width: '50%',
                  height: '50%',
                  borderRadius: '0%',
                  overflow: 'auto',
                  marginTop: '-4%',
                  marginRight: '10%',
                  marginBottom: '-4%',
                }}
              >
                <img src={HeroImage} alt="Software Testing Automation Illustration" className="img-fluid" style={{  // Using placeholder
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }} />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    <TesterBudNavbar />

    
      <Container className="mt-5 pt-5 pb-5 mb-5">
        <Row className="justify-content-center">
          <Col md={8} className="mx-auto">
            <Card className="shadow-lg rounded-lg p-5 bg-light">
              <Card.Body>
                <h2 className="card-title mb-4">Create a New Account</h2>
                <p className="card-text mb-4">
                  Practice automating user registration on our free dummy registration page. This page is designed to help you master the process of automating registration forms, a crucial skill for UI and test automation.  Ideal for learners using Selenium, Cypress, and Playwright.
                </p>
                <p className="card-text mb-4">
                  Here's what you'll gain by practicing here:
                </p>
                <ul className="list-group list-group-flush mb-4">
                  <li className="list-group-item"><strong>Registration Process Automation:</strong> Learn to automate the entire registration flow, from filling out forms to handling submission.</li>
                  <li className="list-group-item"><strong>Form Element Handling:</strong>  Become proficient in interacting with various form elements, including text fields, dropdowns, checkboxes, and radio buttons.</li>
                  <li className="list-group-item"><strong>Validation Testing:</strong>  Practice automating the testing of input validation rules, such as email format, password strength, and required fields.</li>
                  <li className="list-group-item"><strong>Error Handling:</strong> Learn to automate the handling of error messages and ensure that your automation scripts can gracefully handle registration failures.</li>
                </ul>

                <p className="card-text mb-4">
                  Complete the registration form below to practice your automation skills.
                </p>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && (
                  <Card className="text-center">
                    <Card.Body>
                      <div className="d-flex justify-content-center align-items-center mb-3">
                        <CheckCircle size={48} color="#059669" />
                      </div>
                      <Card.Title>Registration Successful</Card.Title>
                      <Card.Text>Your account has been created successfully.</Card.Text>
                      <Link to="/practice-login-form">
                        <Button variant="primary" className="mt-3 w-100">Go to Login</Button>
                      </Link>
                    </Card.Body>
                  </Card>
                )}

                {!success && (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group controlId="email" className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </Form.Group>

                    <Form.Group controlId="password" className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Create a password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </Form.Group>

                    <Form.Group controlId="confirmPassword" className="mb-3">
                      <Form.Label>Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </Form.Group>

                    <div className="bg-light p-3 rounded">
                      <strong>Password must include:</strong>
                      <ListGroup variant="flush" className="mt-2">
                        <ListGroup.Item className={password.length >= passwordRules.minLength ? 'text-success' : 'text-danger'}>
                          At least 8 characters
                        </ListGroup.Item>
                        <ListGroup.Item className={passwordRules.hasUpperCase ? 'text-success' : 'text-danger'}>
                          At least one uppercase letter (A-Z)
                        </ListGroup.Item>
                        <ListGroup.Item className={passwordRules.hasLowerCase ? 'text-success' : 'text-danger'}>
                          At least one lowercase letter (a-z)
                        </ListGroup.Item>
                        <ListGroup.Item className={passwordRules.hasNumber ? 'text-success' : 'text-danger'}>
                          At least one number (0-9)
                        </ListGroup.Item>
                        <ListGroup.Item className={passwordRules.hasSpecialChar ? 'text-success' : 'text-danger'}>
                          At least one special character (!@#$%^&*()_+)
                        </ListGroup.Item>
                      </ListGroup>
                    </div>

                    <Button type="submit" variant="primary" className="mt-3 w-100">
                      Register
                    </Button>
                  </Form>
                )}

                <div className="text-center mt-3">
                  <p>
                    Already have an account?{' '}
                    <Link to="/practice-login-form">Sign in</Link>
                  </p>
                  <Link to="/practice-login-form">
                    <Button variant="outline-dark">Back To Login Page ➡️</Button>
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <footer className="bg-dark text-light text-center py-4 mt-auto">
        <Container>
          <p className="mb-1">Ready to start automating? Explore, practice, and master!</p>
          <small>© 2025 TesterBud | Built for learners by learners 🚀</small>
        </Container>
      </footer>
    </HelmetProvider>
  );
};

export default RegistrationPage;
